/**
 * Client-side shaping of AI usage rows.
 *
 * The `/ai/usage/summary` endpoint aggregates server-side by whatever
 * `group_by` it is sent (see `backend/features/ai_gateway/services/
 * usage_query_service.py::GROUPABLE` -- only user/project/model/provider/
 * client exist). This page always fetches at the finest whitelisted
 * granularity (all five dimensions) and re-groups/filters client-side, so filter and
 * "group by" controls in the UI don't need extra round trips or backend
 * query params the API doesn't have (e.g. there is no `provider` or
 * `user` filter param -- only `project_id`).
 */

import type { SummaryRow } from '@services/aiUsageService';
import { sumNullableCost } from './usageFormat';
import type { UsageDimension, UsageDisplayRow } from './types';

export function toDisplayRow(row: SummaryRow): UsageDisplayRow {
  return {
    user: row.user ?? null,
    userLabel: row.user_label ?? null,
    project: row.project ?? null,
    projectLabel: row.project_label ?? null,
    model: row.model ?? null,
    provider: row.provider ?? null,
    client: row.client ?? null,
    tokensIn: row.tokens_in,
    tokensOut: row.tokens_out,
    cacheReadTokens: row.cache_read_tokens,
    cacheWriteTokens: row.cache_write_tokens,
    requests: row.requests,
    costUsd: row.cost_usd,
  };
}

interface Accumulator {
  dims: Partial<Record<UsageDimension, string | null>>;
  /** Carried alongside `dims`, never grouped ON: grouping stays keyed by
   * id so two people who happen to share a label can't merge into one
   * row, and a row whose label is null can't merge with one whose isn't. */
  labels: { userLabel: string | null; projectLabel: string | null };
  tokensIn: number;
  tokensOut: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  requests: number;
  costs: Array<number | null>;
}

/** Re-aggregates fine-grained rows by the chosen dimensions. */
export function groupRows(rows: UsageDisplayRow[], dims: UsageDimension[]): UsageDisplayRow[] {
  const keys = dims.length > 0 ? dims : (['user'] as UsageDimension[]);
  const groups = new Map<string, Accumulator>();

  for (const row of rows) {
    const id = keys.map((key) => row[key] ?? '').join('\0');
    let acc = groups.get(id);
    if (!acc) {
      acc = {
        dims: {}, labels: { userLabel: row.userLabel, projectLabel: row.projectLabel },
        tokensIn: 0, tokensOut: 0, cacheReadTokens: 0, cacheWriteTokens: 0,
        requests: 0, costs: [],
      };
      for (const key of keys) acc.dims[key] = row[key];
      groups.set(id, acc);
    }
    acc.tokensIn += row.tokensIn;
    acc.tokensOut += row.tokensOut;
    acc.cacheReadTokens += row.cacheReadTokens;
    acc.cacheWriteTokens += row.cacheWriteTokens;
    acc.requests += row.requests;
    acc.costs.push(row.costUsd);
  }

  return Array.from(groups.values())
    .map((acc) => ({
      user: acc.dims.user ?? null,
      userLabel: acc.labels.userLabel,
      project: acc.dims.project ?? null,
      projectLabel: acc.labels.projectLabel,
      model: acc.dims.model ?? null,
      provider: acc.dims.provider ?? null,
      client: acc.dims.client ?? null,
      tokensIn: acc.tokensIn,
      tokensOut: acc.tokensOut,
      cacheReadTokens: acc.cacheReadTokens,
      cacheWriteTokens: acc.cacheWriteTokens,
      requests: acc.requests,
      costUsd: sumNullableCost(acc.costs),
    }))
    .sort((a, b) => {
      // Sort-order only, never displayed -- nulls sort as 0 spend so an
      // all-local group falls to the bottom rather than crashing NaN math.
      const byCost = (b.costUsd ?? 0) - (a.costUsd ?? 0);
      return byCost !== 0 ? byCost : b.tokensIn - a.tokensIn;
    });
}

/**
 * Matches against every spelling of a dimension the user might type: the
 * label they can actually see in the table, and the raw id, which is what
 * the cell shows when no label resolved.
 */
const matches = (values: Array<string | null>, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return values.some((value) => (value ?? '').toLowerCase().includes(needle));
};

export interface UsageFilterValues {
  project: string;
  provider: string;
  user: string;
  client: string;
}

/** Client-side narrowing applied after the date-scoped fetch. */
export function filterRows(rows: UsageDisplayRow[], filters: UsageFilterValues): UsageDisplayRow[] {
  return rows.filter(
    (row) =>
      matches([row.projectLabel, row.project], filters.project) &&
      matches([row.provider], filters.provider) &&
      matches([row.userLabel, row.user], filters.user) &&
      matches([row.client], filters.client)
  );
}
